import React, { useEffect, useState } from "react";
import { api, BACKEND_URL, fmtNum, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const READER_FILTERS = [
  ["ALL", "Tutti"], ["DRAFT", "Bozze"], ["PENDING", "In attesa di approvazione"],
  ["APPROVED", "Approvati"], ["PROD_BLOCKED", "Produzione bloccata"], ["PROD_READY", "Produzione pronta"],
];

const CS_LABEL = { APPROVED: "APPROVATO", PENDING_HUMAN_APPROVAL: "IN ATTESA DI APPROVAZIONE", BOZZA_INCOMPLETA: "BOZZA" };

export function QuoteList() {
  const [list, setList] = useState([]);
  const [arch, setArch] = useState([]);
  const [flt, setFlt] = useState("ALL");
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/quotes").then(({ data }) => setList(data)).catch(() => {});
    api.get("/reader/quotes-archive").then(({ data }) => setArch(data.items || [])).catch(() => {});
  }, []);
  const match = (it) => {
    if (flt === "DRAFT" && it.commercial_status !== "BOZZA_INCOMPLETA") return false;
    if (flt === "PENDING" && it.commercial_status !== "PENDING_HUMAN_APPROVAL") return false;
    if (flt === "APPROVED" && it.commercial_status !== "APPROVED") return false;
    if (flt === "PROD_BLOCKED" && it.production_status !== "BLOCKED_PENDING_CAM_AND_EXACT_PRESS") return false;
    if (flt === "PROD_READY" && it.production_status !== "READY_FOR_RELEASE_CHECK") return false;
    if (q) {
      const s = q.toLowerCase();
      return [it.quote_number, it.customer, it.rfq_reference, it.part_code, it.pdf_filename].some((v) => String(v || "").toLowerCase().includes(s));
    }
    return true;
  };
  const rows = arch.filter(match);
  return (
    <div className="p-8" data-testid="quotes-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Archivio globale</div>
        <h1 className="text-3xl font-display font-bold mt-1">Preventivi</h1>
      </div>
      <div className="border border-border bg-white mb-8" data-testid="reader-quotes-archive">
        <div className="px-4 py-2 border-b border-border flex flex-wrap items-center gap-2">
          <span className="font-display font-bold text-sm">Preventivi da lettura disegno (approvazione umana)</span>
          <span className="text-[10px] text-muted-foreground">archivio append-only · nessuna cancellazione · nessun invio automatico</span>
          <div className="ml-auto flex flex-wrap gap-1 items-center">
            {READER_FILTERS.map(([k, lbl]) => (
              <button key={k} onClick={() => setFlt(k)} data-testid={`qfilter-${k}`}
                className={`border px-2 py-0.5 text-[10px] ${flt === k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}>{lbl}</button>
            ))}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca: numero, cliente, RFQ, codice, PDF…"
              className="border border-border px-2 py-1 text-xs w-64" data-testid="quotes-search" />
          </div>
        </div>
        <table className="dense-table w-full text-xs">
          <thead><tr>{["Numero", "Cliente", "RFQ", "Codice disegno", "Rev.", "Data", "Qtà", "Totale", "Stato commerciale", "Stato produzione", "Autore/Approvatore", "Azioni"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((it) => (
              <tr key={it.quote_number} data-testid={`arch-row-${it.quote_number}`}>
                <td className="font-mono font-bold"><Link to={`/quotes/reader/${it.job_id}`} className="hover:underline" data-testid={`arch-link-${it.quote_number}`}>{it.quote_number}</Link></td>
                <td>{it.customer || "—"}</td>
                <td className="text-[10px]">{it.rfq_reference || "—"}</td>
                <td className="font-mono">{it.part_code}</td>
                <td>rev.{it.revision}</td>
                <td className="text-muted-foreground">{String(it.date || "").slice(0, 10)}</td>
                <td>{it.quantity != null ? Math.round(it.quantity) : "—"}</td>
                <td className="font-bold">{it.total != null ? fmtMoney(it.total) : `— (subtot. ${fmtMoney(it.subtotal)})`}</td>
                <td><span className={`px-1.5 py-0.5 border text-[9px] uppercase font-bold ${it.commercial_status === "APPROVED" ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800"}`}>{CS_LABEL[it.commercial_status] || it.commercial_status}</span></td>
                <td><span className="px-1.5 py-0.5 border border-red-300 bg-red-50 text-red-800 text-[9px] uppercase">{it.production_status === "BLOCKED_PENDING_CAM_AND_EXACT_PRESS" ? "BLOCCATA (CAM+PRESSA)" : "PRONTA AL CONTROLLO"}</span></td>
                <td>{it.author || "—"}</td>
                <td className="whitespace-nowrap">
                  <Link to={`/quotes/reader/${it.job_id}`} className="underline mr-2">Dettaglio</Link>
                  {it.has_final_pdf && it.download_enabled !== false && <a className="underline" href={`${BACKEND_URL}/api/reader/jobs/${it.job_id}/final-quote-pdf`} target="_blank" rel="noreferrer" data-testid={`arch-pdf-${it.quote_number}`}>PDF</a>}
                  {it.integrity_status === "INTEGRITY_BLOCKED" && <span className="border border-red-400 bg-red-50 text-red-800 px-1 text-[9px] font-bold" data-testid={`arch-integrity-${it.quote_number}`}>INTEGRITY_BLOCKED · download disabilitato</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="12" className="text-center py-6 text-muted-foreground">Nessun preventivo per il filtro selezionato.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="border border-border bg-white">
        <div className="px-4 py-2 border-b border-border font-display font-bold text-sm">Preventivi modulo base (legacy)</div>
        <table className="dense-table w-full">
          <thead><tr><th>Numero</th><th>RFQ</th><th>Stato</th><th>Affid.</th><th>Creato</th></tr></thead>
          <tbody>
            {list.map(q2 => (
              <tr key={q2.id}>
                <td className="font-mono"><Link to={`/quotes/${q2.id}`} className="hover:underline">{q2.number}</Link></td>
                <td className="text-xs">{q2.rfq_id?.slice(-6)}</td>
                <td className="uppercase text-[10px] tracking-widest">{q2.status}</td>
                <td><span className={`px-2 py-0.5 border text-[10px] uppercase bg-reliability-${q2.reliability}`}>{q2.reliability}</span></td>
                <td className="text-xs text-muted-foreground">{(q2.created_at||"").slice(0,10)}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-muted-foreground text-sm">Nessun preventivo.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function QuoteDetail() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const { tenant } = useAuth();
  const load = async () => { const { data } = await api.get(`/quotes/${id}`); setQ(data); };
  useEffect(() => { load(); }, [id]);
  if (!q) return <div className="p-8 text-muted-foreground text-sm">Caricamento…</div>;

  const approve = async () => {
    try { await api.post(`/quotes/${id}/approve`); toast.success("Preventivo approvato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const c = q.calculation || {};
  return (
    <div className="p-8" data-testid="quote-detail">
      <Link to="/quotes" className="text-xs text-muted-foreground hover:text-foreground">← Preventivi</Link>
      <div className="flex items-end justify-between mt-2 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{q.description}</div>
          <h1 className="text-3xl font-display font-bold mt-1 font-mono">{q.number}</h1>
        </div>
        <div className="flex gap-2">
          <span className={`px-2 py-1 border text-[10px] uppercase bg-reliability-${q.reliability}`}>{q.reliability}</span>
          <span className="px-2 py-1 border text-[10px] uppercase">{q.status}</span>
          <a href={`${BACKEND_URL}/api/quotes/${id}/pdf`} target="_blank" rel="noreferrer"
             className="border border-border px-3 py-1 text-xs hover:bg-muted flex items-center gap-1" data-testid="btn-pdf">
            <Download size={12} />PDF
          </a>
          <Button size="sm" variant="outline" data-testid="btn-duplicate"
            onClick={async () => {
              try {
                const { data } = await api.post(`/quotes/${id}/duplicate`);
                toast.success(`Duplicato come ${data.number}`);
                window.location.href = `/quotes/${data.id}`;
              } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
            }}>Duplica</Button>
          {q.status !== "approved" && q.reliability !== "red" && (
            <Button size="sm" onClick={approve} data-testid="btn-approve"><CheckCircle2 size={14} className="mr-1" />Approva</Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="border border-border bg-white">
          <div className="px-4 py-3 border-b border-border font-display font-bold text-sm">Righe preventivo</div>
          <table className="dense-table w-full">
            <thead><tr><th>Q.tà</th><th>Peso (kg)</th><th>Prezzo unit.</th><th>Totale</th></tr></thead>
            <tbody>
              {(c.lines || []).map(l => (
                <tr key={l.quantity}>
                  <td className="font-mono font-bold">{l.quantity}</td>
                  <td className="font-mono text-right">{fmtNum(l.weight_kg, 4)}</td>
                  <td className="font-mono text-right">{fmtMoney(l.unit_price, tenant?.currency)}</td>
                  <td className="font-mono text-right font-bold">{fmtMoney(l.line_total, tenant?.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="border border-border bg-white">
          <div className="px-4 py-3 border-b border-border font-display font-bold text-sm">Distinta interna (quantità min.)</div>
          <table className="dense-table w-full">
            <tbody>
              {c.lines?.[0] && Object.entries(c.lines[0].breakdown).map(([k, v]) => (
                <tr key={k}>
                  <td className="text-muted-foreground uppercase text-[10px] tracking-widest">{k.replace(/_/g," ")}</td>
                  <td className="font-mono text-right">{typeof v === "number" ? fmtNum(v, 4) : v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {c.warnings?.length > 0 && (
        <div className="mt-6 border border-warning/50 bg-reliability-yellow p-4">
          <div className="font-bold text-sm mb-1">Avvisi motore</div>
          <ul className="text-xs list-disc pl-4">{c.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}
      <div className="mt-6 text-[11px] text-muted-foreground">
        Engine v{q.engine_version} · nessuna AI utilizzata per il calcolo · formule deterministiche · Beta: verificare i valori prima dell'emissione.
      </div>
    </div>
  );
}

export function ReaderQuoteDetail() {
  const { jobId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/reader/quotes-archive/${jobId}`).then(({ data }) => setD(data)).catch((e) => setErr(e.response?.status || "errore"));
  }, [jobId]);
  if (err) return <div className="p-8 text-muted-foreground text-sm">Preventivo non trovato ({err}).</div>;
  if (!d) return <div className="p-8 text-muted-foreground text-sm">Caricamento…</div>;
  const com = d.commercial || {};
  const Row = ({ l, v, tid }) => (
    <div className="flex justify-between border-b border-border/50 py-1 text-sm"><span className="text-muted-foreground">{l}</span><b data-testid={tid}>{v ?? "—"}</b></div>
  );
  return (
    <div className="p-8 max-w-5xl" data-testid="reader-quote-detail">
      <Link to="/quotes" className="text-xs text-muted-foreground hover:text-foreground">← Preventivi</Link>
      <div className="flex items-end justify-between mt-2 mb-4 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{d.part_code} — {d.part_description}</div>
          <h1 className="text-3xl font-display font-bold mt-1 font-mono">{d.quote_number} <span className="text-base font-sans text-muted-foreground">rev.{d.revision}</span></h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <span className={`px-2 py-1 border text-[10px] uppercase font-bold ${d.commercial_status === "APPROVED" ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800"}`} data-testid="detail-commercial-status">COMMERCIAL_STATUS: {d.commercial_status}</span>
          <span className="px-2 py-1 border border-red-300 bg-red-50 text-red-800 text-[10px] uppercase font-bold" data-testid="detail-production-status">PRODUCTION_STATUS: {d.production_status}</span>
          {d.integrity_status === "INTEGRITY_BLOCKED" && (
            <span className="px-2 py-1 border border-red-500 bg-red-100 text-red-900 text-[10px] uppercase font-bold" data-testid="detail-integrity-blocked">INTEGRITY_BLOCKED — totale ≠ imponibile + IVA · download disabilitato</span>
          )}
          {d.final_pdf_available && (
            <a href={`${BACKEND_URL}/api/reader/jobs/${d.job_id}/final-quote-pdf`} target="_blank" rel="noreferrer"
               className="border border-foreground bg-foreground text-background px-3 py-1.5 text-xs font-bold hover:opacity-90 flex items-center gap-1" data-testid="btn-download-final-pdf">
              <Download size={12} />Scarica PDF definitivo
            </a>
          )}
        </div>
      </div>
      {d.production_block_reasons?.length > 0 && (
        <div className="text-[11px] text-red-800 border border-red-200 bg-red-50 px-3 py-2 mb-4" data-testid="detail-prod-reasons">
          Produzione bloccata (il preventivo commerciale approvato resta valido): {d.production_block_reasons.join(" · ")}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="border border-border bg-white p-4" data-testid="detail-commercial-box">
          <div className="font-display font-bold text-sm mb-2">Dati cliente e condizioni commerciali</div>
          <Row l="Cliente" v={com.customer_name} tid="detail-customer" />
          <Row l="Destinatario" v={com.recipient} />
          <Row l="Validità offerta" v={com.validity_days ? `${com.validity_days} giorni` : null} />
          <Row l="Tempi di consegna" v={com.delivery_terms} />
          <Row l="Condizioni di pagamento" v={com.payment_terms} />
          <Row l="Trasporto" v={com.transport_included == null ? null : com.transport_included ? "incluso" : "escluso"} />
          <Row l="Imballaggio" v={com.packaging_included == null ? null : com.packaging_included ? "incluso" : "escluso"} />
          <Row l="Note commerciali" v={com.commercial_notes || "—"} />
          <Row l="RFQ originale" v={<Link to={`/rfqs/${d.rfq?.id}`} className="underline" data-testid="detail-rfq-link">{d.rfq?.reference}</Link>} />
        </div>
        <div className="border border-border bg-white p-4" data-testid="detail-totals-box">
          <div className="font-display font-bold text-sm mb-2">Importi</div>
          <Row l="Quantità" v={d.quantity != null ? Math.round(d.quantity) : null} tid="detail-qty" />
          <Row l="Materiale" v={d.material} />
          <Row l="Imponibile (totale preventivo)" v={d.grand_total != null ? fmtMoney(d.grand_total) : "—"} tid="detail-total" />
          <Row l="IVA" v={d.vat ? `${d.vat.label}${d.vat.amount != null ? ` · ${fmtMoney(d.vat.amount)}` : ""}` : null} />
          <Row l="Totale documento (imponibile + IVA)" v={d.vat?.total_with_vat != null ? fmtMoney(d.vat.total_with_vat) : "—"} />
          {d.approved_by && (
            <div className="mt-3 text-[11px] text-emerald-800 border-t border-border pt-2" data-testid="detail-approval-info">
              Approvato da <b>{d.approved_by}</b> il {String(d.approved_at).slice(0, 10)} — motivazione: {d.approval_reason}
            </div>
          )}
          <div className="mt-2 text-[10px] text-muted-foreground">{d.note}</div>
        </div>
      </div>
      <div className="border border-border bg-white p-4 mb-6" data-testid="detail-lines">
        <div className="font-display font-bold text-sm mb-2">Righe del preventivo</div>
        <table className="w-full text-xs">
          <thead><tr className="border-b border-border bg-muted text-left"><th className="px-2 py-1">Descrizione</th><th className="px-2 py-1">Qtà</th><th className="px-2 py-1">Importo</th></tr></thead>
          <tbody>{(d.client_lines || []).map((l, i) => (
            <tr key={i} className="border-b border-border/50"><td className="px-2 py-1">{l.description}</td><td className="px-2 py-1">{l.qty != null ? `${l.qty} ${l.unit || ""}` : "—"}</td><td className="px-2 py-1 font-bold">{l.total != null ? fmtMoney(l.total) : "—"}</td></tr>
          ))}</tbody>
        </table>
      </div>
      <div className="border border-border bg-white p-4" data-testid="detail-history">
        <div className="font-display font-bold text-sm mb-2">Storico versioni (append-only)</div>
        <table className="w-full text-[11px]">
          <thead><tr className="border-b border-border bg-muted text-left">{["Versione", "Autore", "Data", "Motivo", "Totale", "Variazioni", "Stato", "PDF"].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}</tr></thead>
          <tbody>{(d.history || []).map((en, i) => (
            <tr key={i} className="border-b border-border/50 align-top" data-testid={`detail-history-${en.kind}-${en.revision}`}>
              <td className="px-2 py-1 font-bold whitespace-nowrap">{en.kind === "DOCUMENT_RENDER_REVISION" ? <span>FORMATO DOCUMENTO <span className="border border-sky-400 bg-sky-50 text-sky-800 px-1 text-[9px]" data-testid="detail-render-badge">IVA ESCLUSA · render {en.render_revision}</span></span> : en.kind === "FINAL_COMMERCIAL_QUOTE" ? (en.version_status === "SUPERSEDED_BY_CORRECTION" ? <span>DEFINITIVO <span className="border border-red-400 bg-red-50 text-red-800 px-1 text-[9px]" data-testid="detail-superseded-badge">VERSIONE SUPERATA</span></span> : "DEFINITIVO (approvato)") : `rev.${en.revision}`}</td>
              <td className="px-2 py-1">{en.author}</td>
              <td className="px-2 py-1 whitespace-nowrap">{String(en.date).slice(0, 16).replace("T", " ")}</td>
              <td className="px-2 py-1">{en.reason}</td>
              <td className="px-2 py-1 font-bold whitespace-nowrap">{en.total != null ? fmtMoney(en.total) : `— (subtot. ${fmtMoney(en.subtotal)})`}</td>
              <td className="px-2 py-1">{(en.changes || []).length ? en.changes.map((c2) => `${c2.category}: ${c2.before.status || "—"}→${c2.after.status}`).join(" · ") : "—"}</td>
              <td className="px-2 py-1">{en.status}</td>
              <td className="px-2 py-1">{en.kind === "DOCUMENT_RENDER_REVISION"
                ? <a className="underline font-bold" href={`${BACKEND_URL}${en.pdf}`} target="_blank" rel="noreferrer" data-testid="history-render-pdf">PDF cliente (IVA esclusa)</a>
                : en.kind === "FINAL_COMMERCIAL_QUOTE"
                ? <a className="underline font-bold" href={`${BACKEND_URL}${en.pdf}`} target="_blank" rel="noreferrer" data-testid={en.version_status === "SUPERSEDED_BY_CORRECTION" ? "history-superseded-pdf" : "history-final-pdf"}>{en.version_status === "SUPERSEDED_BY_CORRECTION" ? "PDF storico (VERSIONE SUPERATA)" : "Scarica PDF definitivo"}</a>
                : <a className="underline" href={`${BACKEND_URL}${en.pdf}`} target="_blank" rel="noreferrer">bozza</a>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
